import React, { useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { makeObservable, observable, action, computed } from "mobx";
import styles from "./CountryCodeSelect.module.scss";
import { useLocalStoreWithInjectedDependencies } from "@whatslly/commons/di";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import classnames from "classnames";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import type { CountriesType } from "@whatslly/commons/phoneUtils/countries";
import { COUNTRIES } from "@whatslly/commons/phoneUtils/countries";
import sortBy from "lodash/sortBy";
import { useTranslation } from "react-i18next";

export type CountryCodesCountType = {
  countryCode: string;
  count: number;
};
export type SelectedCountriesType = {
  country: string;
  code: string;
}[];

interface CountryCodeSelectProps {
  testId?: string;
  handleCountryCodeChange: (code: string) => void;
  selectedCountryCodes?: SelectedCountriesType;
  mostUsedCountryCodes?: CountryCodesCountType[];
  countryCode?: string;
  className?: string;
  disableAutoPick?: boolean;
  disableBackgroundColor?: boolean;
}

export interface CountryCodeOption extends CountriesType {
  label: string;
}

export class CountryCodeSelectStore {
  isOpen = false;
  selectedOption: CountryCodeOption | null = null;
  searchTerm = "";
  selectedCountryCodes: SelectedCountriesType = [];
  mostUsedCountryCodes: CountryCodesCountType[] = [];
  lang = "en";

  constructor() {
    makeObservable(this, {
      isOpen: observable,
      selectedOption: observable.ref,
      countryCodeOptions: computed,
      searchTerm: observable,
      selectedCountryCodes: observable.ref,
      mostUsedCountryCodes: observable.ref,
      filteredCountryCodeOptions: computed,
      noResultsFound: computed,
      toggleOpen: action.bound,
      handleOptionClick: action.bound,
      handleSearchTermChange: action.bound,
      init: action.bound,
      lang: observable,
    });
  }

  init(selectedCountryCodes: SelectedCountriesType, mostUsedCountryCodes: CountryCodesCountType[], lang: string) {
    this.selectedCountryCodes = selectedCountryCodes;
    this.mostUsedCountryCodes = mostUsedCountryCodes;
    this.lang = lang;
  }

  toggleOpen() {
    this.isOpen = !this.isOpen;
  }

  handleOptionClick(option: CountryCodeOption) {
    this.selectedOption = option;
    this.isOpen = false;
  }

  handleSearchTermChange(event?: React.ChangeEvent<HTMLInputElement>, value?: string) {
    this.searchTerm = value || event?.target.value || "";
  }

  get displayValue() {
    return this.selectedOption?.text || "+ ";
  }

  get noResultsFound() {
    return this.filteredCountryCodeOptions.length === 0 && this.searchTerm.length > 0;
  }

  private sortFilteredOptions(filteredOptions: CountryCodeOption[]): CountryCodeOption[] {
    const onlySelectedCountryCodes = filteredOptions.filter((option) =>
      this.selectedCountryCodes.some((selectedCountry) => selectedCountry.code === option.code && selectedCountry.country === option.country)
    );
    const onlyNotSelectedCountryCodes = filteredOptions.filter(
      (option) =>
        !this.selectedCountryCodes.some((selectedCountry) => selectedCountry.code === option.code && selectedCountry.country === option.country)
    );
    const sortedSelectedCountryCodes = sortBy(onlySelectedCountryCodes, [
      (option) => {
        const index = this.selectedCountryCodes.findIndex(
          (selectedCountry) => selectedCountry.code === option.code && selectedCountry.country === option.country
        );
        const mostUsedIndex = this.mostUsedCountryCodes.findIndex((mostUsedCountry) => mostUsedCountry.countryCode === option.code);
        return mostUsedIndex !== -1 ? mostUsedIndex : index !== -1 ? onlySelectedCountryCodes.length : Infinity;
      },
      `country`,
    ]);
    return [...sortedSelectedCountryCodes, ...sortBy(onlyNotSelectedCountryCodes, [`country`])];
  }

  get countryCodeOptions() {
    return COUNTRIES.map((country) => {
      const es = this.lang === "es" ? country.countryES : country.country;
      const countryText = this.lang === "pt" ? country.countryPT : es;
      return {
        ...country,
        label: `${country.flag} ${countryText} (${country.text})`,
      };
    });
  }

  get filteredCountryCodeOptions(): CountryCodeOption[] {
    const filteredOptions = this.countryCodeOptions.filter((option) => option.label.toLowerCase().includes(this.searchTerm.toLowerCase()));
    return this.sortFilteredOptions(filteredOptions);
  }
}

export const CountryCodeSelect: React.FC<CountryCodeSelectProps> = observer(
  ({ handleCountryCodeChange, selectedCountryCodes, mostUsedCountryCodes, countryCode, disableAutoPick, disableBackgroundColor, testId }) => {
    const { t, i18n } = useTranslation();
    const store = useLocalStoreWithInjectedDependencies(CountryCodeSelectStore);
    const handleSelect = useCallback(
      (option: CountryCodeOption) => {
        handleCountryCodeChange(option.code);
        store.handleOptionClick(option);
      },
      [handleCountryCodeChange, store]
    );

    useEffect(() => {
      if (selectedCountryCodes && mostUsedCountryCodes) store.init(selectedCountryCodes, mostUsedCountryCodes, i18n.language);
      if (!disableAutoPick) {
        handleSelect(store.filteredCountryCodeOptions.find((country) => country.code === countryCode) || store.filteredCountryCodeOptions[0]);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mostUsedCountryCodes, selectedCountryCodes, i18n.language]);

    useEffect(() => {
      store.handleSearchTermChange(undefined, "");
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.isOpen]);

    return (
      <div className={classnames(styles.countryCodeSelect, styles.forceLightTheme, { [styles.disableBackgroundColor]: disableBackgroundColor })}>
        <div data-testid={`${testId ? `${testId}_country_select_button` : ""}`} className={styles.selectContainer} onClick={store.toggleOpen}>
          <div className={styles.selectedOption}>{store.displayValue}</div>
          <div className={styles.arrowIcon}>
            <Svg iconName="arrow_down" className={classnames(styles.arrow, { [styles.rotateArrow]: store.isOpen })} />
          </div>
        </div>
        {store.isOpen && (
          <div className={styles.optionsWrapper}>
            <div className={styles.searchBar}>
              <TextInput
                data-testid={`${testId ? `${testId}_country_select_search_input` : ""}`}
                placeholder={`Search countries`}
                value={store.searchTerm}
                onChange={store.handleSearchTermChange}
                borderRadius={`none`}
                paddingInline="none"
                autoFocus
                backgroundColor={`neutral-tertiary`}
                onClear={() => store.handleSearchTermChange(undefined, "")}
              />
            </div>
            <div className={styles.optionsContainer}>
              {store.noResultsFound && <div className={styles.noResults}>{t("chat_now_modal.search_no_resoults_found")}</div>}
              {store.filteredCountryCodeOptions.map((option) => (
                <div
                  data-testid={`${testId ? `${testId}_country_select_option` : ""}`}
                  key={`${option.text}-${option.country}`}
                  className={classnames(styles.option, { [styles.selected]: option.country === store.selectedOption?.country })}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);
